import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Container,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';

import { useNoteAuditsList } from '../../hooks/useNoteAuditsList';
import { useGetProviderSummaryQuery } from '../../store/api';

import { NoteAuditTableHeader, NoteAuditTableRow } from './detail/NoteAuditTableRow';
import { ProviderDetailHeader } from './detail/ProviderDetailHeader';
import { ProviderDetailStats } from './detail/ProviderDetailStats';

export const ProviderDetail: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { jotauditDashboard } = useEventTracking();

  const [timeRange, setTimeRange] = useState('30days');

  // Sub-tabs state
  const currentSubTab = searchParams.get('subtab') || 'all';
  const handleSubTabChange = (_: any, newValue: string) => {
    setSearchParams(prev => {
      prev.set('subtab', newValue);
      return prev;
    });
  };

  const {
    data: provider,
    isLoading: isProviderLoading,
    error: providerError,
    refetch: refetchProvider,
  } = useGetProviderSummaryQuery({ userId: userId! }, { skip: !userId });

  // Note audits list for this provider
  const { 
    audits, 
    isLoading: isNotesLoading, 
    error: notesError, 
    refetch: refetchNotes,
    isEmpty 
  } = useNoteAuditsList({ userId });

  // Track provider detail viewed
  const viewedTrackedRef = useRef<string | null>(null);
  useEffect(() => {
    if (provider && userId && viewedTrackedRef.current !== userId) {
      viewedTrackedRef.current = userId;
      jotauditDashboard.providerDetailViewed({
        provider_id: userId,
        total_notes_audited: provider.total_notes_audited,
        avg_quality_score: provider.avg_quality_score ?? undefined,
      });
    }
  }, [provider, userId, jotauditDashboard]);

  const handleBack = () => {
    navigate('/jotaudit?tab=providers');
  };

  const handleViewAudit = (noteId: string) => {
    navigate(`/jotaudit/note/${noteId}`);
  };

  if ((isProviderLoading && !provider) || isNotesLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (providerError || notesError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => { refetchProvider(); refetchNotes(); }}>
              Retry
            </Button>
          }
        >
          <AlertTitle>Error loading provider details</AlertTitle>
          Failed to load information
        </Alert>
      </Box>
    );
  }

  const displayName = provider?.provider_name || 'Marissa Dalton';
  const email = (provider as any)?.email || 'marissa.dalton@riverstoneclinic.com';

  return (
    <Box sx={{ bgcolor: '#F8F9FB', minHeight: '100vh', p: 3, mt: -3, mx: -3 }}>
      <Container maxWidth="xl">
        {/* Breadcrumbs / Header */}
        <ProviderDetailHeader
          name={displayName}
          email={email}
          status="Active"
          onBack={handleBack}
        />

        {/* Sub-Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={currentSubTab}
            onChange={handleSubTabChange}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
              '& .MuiTab-root': {
                textTransform: 'none',
                minWidth: 'auto',
                px: 3,
                fontSize: '0.9375rem',
                fontWeight: 500,
                color: theme.palette.text.secondary,
                '&.Mui-selected': { color: theme.palette.primary.main, fontWeight: 600 },
              },
            }}
          >
            <Tab value="all" label="All Notes" />
            <Tab value="needs_improvement" label="Needs Improvement" />
            <Tab value="drafts" label="Drafts" />
            <Tab value="claims" label="Claim History" />
          </Tabs>
        </Box>

        {/* Controls */}
        <Box sx={{ mb: 3 }}>
          <Select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            size="small"
            IconComponent={ArrowDownIcon}
            sx={{
              bgcolor: 'background.paper',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '0.875rem',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
              '& .MuiSelect-select': {
                py: 1,
                px: 2,
                display: 'flex',
                alignItems: 'center',
                textTransform: 'uppercase',
              },
            }}
          >
            <MenuItem value="7days">Past 7 Days</MenuItem>
            <MenuItem value="30days">Past 30 Days</MenuItem>
            <MenuItem value="90days">Past 90 Days</MenuItem>
          </Select>
        </Box>

        {/* Stats Row */}
        <ProviderDetailStats />

        {/* Notes Table */}
        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          <NoteAuditTableHeader />

          {isNotesLoading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress size={32} />
            </Box>
          ) : isEmpty ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Typography color="text.secondary">No notes found for this provider.</Typography>
            </Box>
          ) : (
            <Box>
              {audits.map((audit, index) => (
                <NoteAuditTableRow
                  key={audit.audit.note_audit_id}
                  auditListItem={audit}
                  onClick={() => handleViewAudit(audit.audit.note_id)}
                  needsImprovement={currentSubTab === 'needs_improvement' || (index === 1 && currentSubTab === 'all')}
                />
              ))}
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default ProviderDetail;
